import React, { useEffect, useState } from "react";
import { api, rupee } from "../lib/api";
import { Plus, Search, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", phone: "", email: "", address: "", city: "", state: "", pincode: "" };

export default function Customers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/customers", { params: q ? { q } : {} }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [q]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/customers", form);
      toast.success("Customer added");
      setForm(empty); setShowAdd(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="customers-root">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl">Customers</h1>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>Store addresses & phone numbers of your buyers.</p>
        </div>
        <button data-testid="cust-add-btn" onClick={() => setShowAdd(true)} className="inline-flex items-center gap-2 px-5 h-12 rounded-md text-white tactile-btn font-semibold" style={{ background: "var(--primary)" }}>
          <Plus size={18} /> Add customer
        </button>
      </div>

      <div className="card-soft p-4 mb-4 flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
          <input data-testid="cust-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or phone" className="h-12 pl-10 pr-3 w-full rounded-md border bg-white" style={{ borderColor: "var(--border-soft)" }} />
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && <div className="col-span-full text-center py-10 card-soft" style={{ color: "var(--text-muted)" }}>No customers yet.</div>}
        {items.map((c) => (
          <div key={c.id} className="card-soft p-5" data-testid={`cust-row-${c.phone}`}>
            <div className="font-display font-semibold text-lg">{c.name}</div>
            <div className="flex items-center gap-2 text-sm mt-1" style={{ color: "var(--text-muted)" }}><Phone size={14} /> {c.phone}</div>
            {(c.address || c.city) && <div className="flex items-start gap-2 text-sm mt-1" style={{ color: "var(--text-muted)" }}><MapPin size={14} className="mt-0.5" /> {[c.address, c.city, c.state, c.pincode].filter(Boolean).join(", ")}</div>}
            <div className="mt-3 flex items-center gap-4 text-xs">
              <div><span className="font-semibold num">{rupee(c.total_spent || 0)}</span> <span style={{ color: "var(--text-muted)" }}>spent</span></div>
              <div><span className="font-semibold num">{c.visits || 0}</span> <span style={{ color: "var(--text-muted)" }}>visits</span></div>
            </div>
          </div>
        ))}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setShowAdd(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-xl p-6 w-full max-w-lg">
            <h2 className="font-display font-bold text-xl mb-4">Add customer</h2>
            <div className="grid grid-cols-2 gap-3">
              <F label="Name*" v={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="add-cust-name" />
              <F label="Phone*" v={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required testid="add-cust-phone" />
              <F label="Email" v={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="add-cust-email" />
              <F label="City" v={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="add-cust-city" />
              <div className="col-span-2"><F label="Address" v={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="add-cust-address" /></div>
              <F label="State" v={form.state} onChange={(v) => setForm({ ...form, state: v })} testid="add-cust-state" />
              <F label="Pincode" v={form.pincode} onChange={(v) => setForm({ ...form, pincode: v })} testid="add-cust-pincode" />
            </div>
            <div className="flex gap-3 mt-5">
              <button type="button" onClick={() => setShowAdd(false)} className="h-11 px-5 rounded-md border" style={{ borderColor: "var(--border-soft)" }}>Cancel</button>
              <button data-testid="add-cust-save" className="h-11 px-6 rounded-md text-white tactile-btn font-semibold" style={{ background: "var(--primary)" }}>Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function F({ label, v, onChange, required, testid }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <input data-testid={testid} required={required} value={v} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-md border bg-white" style={{ borderColor: "var(--border-soft)" }} />
    </div>
  );
}
